import { routes_constants } from "./constants";
import { customGetItem, customSetItem, getAccessToken } from "./local-storage.service";
import { getTimezoneOffset } from "./common";
import { showReloadModal } from "../redux/actions/global.action";
import type { RootState } from "../redux/reducers";

interface HttpApiParams {
  access_token?: string | null;
  api: string;
  body?: Record<string, any>;
  type?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'FORM';
  content_type?: string;
}

export interface HttpApiResponse<T = any> {
  data?: T;
  is_error: number;
  original_text: string;
  flag: number;
  status: number;
  message?: string;
  tracking_id?: string;
  request_id?: number;
  err?: string;
  original_override_text?: string;
  override_text?: string;
}

const baseUrl = process.env.REACT_APP_API_URL || "https://testcustomer.financemutual.com/api/customer/";
// const baseUrl = "https://testcustomer.financemutual.com/api/customer/";

// Store is injected from index.tsx; importing it here would create a circular import
interface InjectedStore {
  getState: () => RootState;
  dispatch: (action: ReturnType<typeof showReloadModal>) => unknown;
}

let appStore: InjectedStore | undefined;

export const injectStore = (store: InjectedStore) => {
  appStore = store;
}

// 0 = no response at all (network down, DNS, connection refused, CORS-blocked)
const SERVER_UNREACHABLE_STATUSES = [0, 502, 503, 504];

const notifyIfServerUnreachable = (status: number) => {
  if (!SERVER_UNREACHABLE_STATUSES.includes(status) || !appStore || appStore.getState().shared.reloadModalOpen) return;
  appStore.dispatch(showReloadModal(true));
}

const get_device_id = () => {
  let device_id = customGetItem('device_id');
  if (!device_id) {
    device_id = guid();
    customSetItem('device_id', device_id);
  }
  return device_id;
}

// const headerDefaults = {
//   app_version: 130,
//   app_type: 10,
//   device_token: "Website",
//   device_type: 0,
// }

async function httpApiCall(params: HttpApiParams): Promise<any> {
  let access_token: any = params.access_token || getAccessToken();
  // params.body = { ...defaultAppDefaults, ...params.body };
  params.body = { ...params.body };
  params.body = params.type === 'POST' ? { ...params.body, ...{ device_info: device_info() } } : params.body;

  if (!customGetItem('user')) {
    const guidLog = guid();
    customSetItem('user', guidLog);
  }
  
  const timezoneOffset = getTimezoneOffset();

  const headers: Record<string, string> = {
    Accept: "*/*",
    "x-client-url": window.location.href,
    "x-lan-code": "en",
    "device_id": get_device_id(),
    "device_type": '0',
    "app_version": '130',
    "app_type": '10',
    "device_token": "Website",
    "Authorization": access_token,
    'X-Time-Zone': Intl.DateTimeFormat().resolvedOptions().timeZone,
    'timezone': timezoneOffset
  };

  let body: any = params.body || {};

  let url = baseUrl + params.api;

  if (params.type === 'FORM') {
    const formData = new FormData();
    Object.keys(body).forEach(key => {
      const value = body[key];
      if (value !== undefined && value !== null) {
        formData.append(key, value as string | Blob);
      }
    });
    body = formData;
    params.type = 'POST';
  } else if (params.type && params.type !== 'GET') {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(body);
  } else {
    body = undefined; // Clear the body for GET requests
  }

  // Retry logic
  const maxRetries = 1;
  let attempt = 0;
  while (attempt < maxRetries) {
    let responseReceived = false;
    try {
      let response: any = await fetch(url, {
        method: params.type || "GET",
        headers: headers,
        body: body
      });
      responseReceived = true;
      notifyIfServerUnreachable(response.status);


      // let data :any =response.json;
      if (!response.ok) {
        // Handle non-2xx status codes
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      // else{
      //   if (data && data.is_error == 1 && (data.flag == 500 || data.flag == 170)) {
      //     localStorage.clear();
      //   <Navigate to="/login" replace={true} />;
      //  }

      //  }

      response = await response.json();
      if (response.flag === 500 || response.flag === 170 || response.flag === 401 || response.flag === 504 || response.flag === 505) {
        localStorage.clear();
        window.location.href = `${routes_constants.login}?auth=expired`;
        return;
      }

      return response;
    } catch (error) {
      // fetch() itself rejects with a TypeError when the server can't be reached
      if (!responseReceived && error instanceof TypeError) {
        notifyIfServerUnreachable(0);
      }
      attempt++;
      if (attempt >= maxRetries) {
        console.error('API call failed after 3 attempts:', error);
        return { message: 'Failed to fetch data after multiple attempts' };
      }
    }
  }

  return { message: 'Unexpected error occurred' };
}

const device_info = () => {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  var window: any = window;
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  var document: any = document;
  // Opera 8.0+
  const isOpera =
    navigator.userAgent.indexOf(' OPR/') >= 0 ||
    navigator.userAgent.indexOf('Opera') >= 0;
  // Firefox 1.0+
  const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
  // Safari 3.0+ "[object HTMLElementConstructor]"
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  // Edge 20+
  const isEdge = navigator.userAgent.indexOf('Edge') > -1;
  // Internet Explorer 6-11
  const isIE =
    typeof document !== 'undefined' && !!document.documentMode && !isEdge;
  // Chrome 1 - 71
  const isChrome = !isOpera && !isEdge && !isSafari;
  // Blink engine detection
  const isBlink = (isChrome || isOpera) && window && !!window.CSS;
  const browserInfo = {
    isFirefox,
    isChrome,
    isSafari,
    isOpera,
    isIE,
    isEdge,
    isBlink,
  };
  const platform =
    navigator && navigator.platform ? navigator.platform : 'unknown';
  const userAgent =
    navigator && navigator.userAgent ? navigator.userAgent : 'unknown';
  return {
    browser: browserInfo,
    platform,
    userAgent,
  };
}

const guid = () => {
  const a = new Date();
  const nav = window.navigator;
  const screen = window.screen;
  let guid = nav.mimeTypes.length.toString();
  guid += nav.userAgent.replace(/\D+/g, '');
  guid += nav.plugins.length;
  guid += screen.height || '';
  guid += screen.width || '';
  guid += screen.pixelDepth || '';
  guid += a.getTime();
  return guid;
}

export async function downloadApi(params: HttpApiParams) {
  let responseReceived = false;
  try {
    let access_token: any = getAccessToken();

    const timezoneOffset = getTimezoneOffset();

    const headers: Record<string, string> = {
      Accept: "*/*",
      "x-client-url": window.location.href,
      "x-lan-code": "en",
      "device_id": get_device_id(),
      "Authorization": access_token,
      'X-Time-Zone': Intl.DateTimeFormat().resolvedOptions().timeZone,
      'timezone': timezoneOffset
    };

    let url = baseUrl + params.api;
    const fetchOptions: any = {
      method: params.type,
      responseType: 'blob' as 'json', 
      observe: 'response' as 'body',
      headers
    };
  
    if (params.type === 'POST' && params.body) {
      fetchOptions.body = params.body;
    } else  if (params.type === 'GET' && params.body){
      const body = new URLSearchParams(params.body).toString();
      url = `${url}?${body}`;
    }
    let response: any = await fetch(url, fetchOptions);
    responseReceived = true;
    notifyIfServerUnreachable(response.status);

    return response;
  } catch (error) {
    if (!responseReceived && error instanceof TypeError) {
      notifyIfServerUnreachable(0);
    }
    console.error(error);
  }
}

export default httpApiCall;
